import { useAsync, useMountEffect } from "@react-hookz/web";
import YouTube from "@vip30/react-youtube";
import { useAtomValue } from "jotai";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import { safeJsonParse } from "scmp-sdk";
import type { YouTubePlayer } from "youtube-player/dist/types";

import { data as appData, config } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import { onlyOnePlayingPlayerAtom } from "scmp-app/components/article/article-list/hooks";
import { VideoPlayMode } from "scmp-app/components/scmp-youtube-video/enums";
import { useAdVolumeControl } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-player/hooks/ad-integration/hooks/ad-volume-control";
import { useImaIntegration } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-player/hooks/ad-integration/hooks/ima-integration";
import type { VideoPlayerYouTubeEventCustomizedParameters } from "scmp-app/components/tracking/google-analytics-4/types";
import type { scmpYoutubeVideoPlayerQuery } from "scmp-app/queries/__generated__/scmpYoutubeVideoPlayerQuery.graphql";

import { getPlayerOptions } from "./helpers";
import { useCheckManualPauseEffect, useIntersectionHandler, useYouTubeControl } from "./hooks";
import {
  AdContainer,
  AdControlButtonContainer,
  Container,
  StyledMuteIcon,
  StyledUnmuteIcon,
  StyledYouTube,
  VideoContainer,
} from "./styles";
import type { VideoOrientation, YoutubeVolumeData } from "./types";

export type Props = {
  adInfo?: ADInfo;
  allow?: string;
  allowfullscreen?: string;
  className?: string;
  controls?: boolean;
  enableAutoPlay: boolean;
  entityId?: string;
  frameborder?: string;
  gaData?: {
    ga4ArticleEntityId?: string;
    ga4Position?: VideoPlayerYouTubeEventCustomizedParameters["position"];
  };
  height?: string;
  index: number;
  isEnabledImaManager?: boolean;
  isMuteVideo?: boolean;
  onDimensionMeasured?: (dimension: string) => void;
  onEnded?: () => void;
  onPlaybackVisible?: () => void;
  onVolumeChanged?: (data: YoutubeVolumeData) => void;
  orientation?: VideoOrientation;
  playerRef?: React.Ref<YouTube>;
  relatedChannels?: string[];
  title?: string;
  videoId: string;
  videoPlayMode: VideoPlayMode;
  width?: string;
};

export const SCMPYouTubeVideoPlayer: FunctionComponent<Props> = ({
  adInfo,
  className,
  controls,
  enableAutoPlay,
  entityId,
  gaData,
  height,
  index,
  isEnabledImaManager = true,
  isMuteVideo = true,
  onDimensionMeasured,
  onEnded,
  onPlaybackVisible,
  onVolumeChanged,
  orientation,
  playerRef,
  relatedChannels = [appData.youtube.scmpChannelId],
  title,
  videoId,
  videoPlayMode,
  width,
}) => {
  const environment = useRelayEnvironment();
  const [{ result: data }, { execute }] = useAsync(() =>
    fetchQuery<scmpYoutubeVideoPlayerQuery>(
      environment,
      graphql`
        query scmpYoutubeVideoPlayerQuery($applicationId: String!, $entityId: String!) {
          content(
            contentType: VIDEO
            filter: { entityId: $entityId, applicationId: $applicationId }
          ) {
            headline
            sectionsV2 {
              value {
                ... on Section {
                  name
                }
                ... on VideoSection {
                  name
                }
              }
            }
            ... on Video {
              orientation
            }
          }
        }
      `,
      {
        applicationId: appData.application.scmp.entityUuid,
        entityId: entityId ?? "",
      },
      {
        fetchPolicy: "store-or-network",
      },
    ).toPromise(),
  );

  useMountEffect(() => {
    if (!notEmpty(entityId)) return;
    void execute();
  });

  // Can play same video in different places
  const uniqueVideoId = ["yt", videoId, entityId, index].filter(notEmpty).join("-");
  const adElementId = `ad-${uniqueVideoId}`;

  const [measuredDimension, setMeasuredDimension] = useState<string | undefined>();
  const [isImaInitialized, setIsImaInitialized] = useState(false);
  const [player, setPlayer] = useState<YouTubePlayer>();

  const trackingData = useMemo(
    () => ({
      dimension: measuredDimension ?? (height && width ? `${width} x ${height}` : undefined),
      ga4ArticleEntityId: gaData?.ga4ArticleEntityId,
      ga4Position: gaData?.ga4Position ?? "article_body",
      sectionString:
        first(data?.content?.sectionsV2)
          ?.value?.map(section => section?.name)
          .join("|") ?? "",
      videoEntityId: entityId ?? "",
      youTubeVideoId: videoId,
      youTubeVideoTitle: title ?? "",
    }),
    [
      data?.content?.sectionsV2,
      entityId,
      gaData?.ga4ArticleEntityId,
      gaData?.ga4Position,
      height,
      measuredDimension,
      title,
      videoId,
      width,
    ],
  );

  const zone = adInfo?.zone ?? `video/article/instream1`;
  const adIu = `${config.advertisement.googlePublisherTag.networkId}/${config.advertisement.googlePublisherTag.adUnitPrefix}/${zone}`;
  const isManualPlay = videoPlayMode === VideoPlayMode.Manual;

  const { stopYoutubeAutoplay } = useAtomValue(onlyOnePlayingPlayerAtom);
  const wrappedEnableAutoplay = useMemo(
    () =>
      !stopYoutubeAutoplay &&
      ((enableAutoPlay && videoPlayMode === VideoPlayMode.Autoplay) || isManualPlay),
    [enableAutoPlay, videoPlayMode, isManualPlay, stopYoutubeAutoplay],
  );

  const playerOptions = useMemo(
    () =>
      getPlayerOptions({
        adIu,
        controls,
        enableAutoPlay: wrappedEnableAutoplay,
        height,
        relatedChannels,
        width,
      }),
    [adIu, controls, height, relatedChannels, width, wrappedEnableAutoplay],
  );

  const handleEnded = useCallback(() => {
    setPlayer(undefined);
    onEnded?.();
  }, [onEnded]);

  const handleImaInitialized = useCallback(() => {
    setIsImaInitialized(true);
  }, []);

  const { adsManager, initImaManager, isAdPlaying, isAdStarted } = useImaIntegration({
    adData: {
      adInfo,
      iu: adIu,
    },
    onInitialized: handleImaInitialized,
    orientation,
    player,
    trackingData,
  });

  const { handleChangeVolume, isMute } = useAdVolumeControl({ adsManager, player });

  const {
    captureIntersectionElement,
    handleIsIntersecting,
    handleIsNotIntersecting,
    intersection,
  } = useIntersectionHandler({
    adsManager,
    isAdPlaying,
    isAdStarted,
    player,
  });

  const {
    handleOnYoutubeInteractEnd,
    handleOnYoutubeInteractStart,
    handleOnYoutubePlayerStateChange,
    isManualPaused,
  } = useCheckManualPauseEffect({
    adsManager,
    isAdStarted,
  });

  const { handleMuteTracking, handleOnReady, handleOnStateChange } = useYouTubeControl({
    player,
    trackingData,
    uniqueVideoId,
  });

  const [youtubeIframe, setYoutubeIframe] = useState<HTMLIFrameElement>();
  const previousMutedReference = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    const handler = (event: MessageEvent<string>) => {
      if (event.source !== youtubeIframe?.contentWindow) return;

      const data = safeJsonParse<YoutubeVolumeData>(event.data);
      if (data?.event !== "infoDelivery" || data?.info?.muted === undefined) return;

      if (data.info.muted && previousMutedReference.current === false) {
        void handleMuteTracking();
      }
      previousMutedReference.current = data.info.muted;
      onVolumeChanged?.(data);
    };
    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
    };
  }, [handleMuteTracking, onVolumeChanged, youtubeIframe?.contentWindow]);

  useEffect(() => {
    if (!youtubeIframe) return;

    const { clientHeight, clientWidth } = youtubeIframe;

    if (clientWidth === 0 || clientHeight === 0) return;

    const isPortraitVideo = data?.content?.orientation === "portrait";
    const iframeIsLandscape = clientWidth > clientHeight;

    let dimension: string;
    if (isPortraitVideo && iframeIsLandscape) {
      const actualWidth = Math.round((clientHeight * 9) / 16);
      dimension = `${actualWidth} x ${clientHeight}`;
    } else if (!isPortraitVideo && !iframeIsLandscape) {
      const actualHeight = Math.round((clientWidth * 9) / 16);
      dimension = `${clientWidth} x ${actualHeight}`;
    } else {
      dimension = `${clientWidth} x ${clientHeight}`;
    }

    setMeasuredDimension(dimension);
    onDimensionMeasured?.(dimension);
  }, [data?.content?.orientation, onDimensionMeasured, youtubeIframe]);

  useEffect(() => {
    if (isAdStarted) onPlaybackVisible?.();
  }, [isAdStarted, onPlaybackVisible]);

  useEffect(() => {
    const isIntersectionAndAdAndPlayerReady =
      (isImaInitialized || !isEnabledImaManager) && !!intersection && !!player;
    if (!isIntersectionAndAdAndPlayerReady) return;

    if (!intersection.isIntersecting) {
      void handleIsNotIntersecting();
      return;
    }

    const shouldTriggerIntersect = !isManualPaused && (isManualPlay || wrappedEnableAutoplay);
    if (shouldTriggerIntersect) {
      void handleIsIntersecting();
    }
  }, [
    handleIsIntersecting,
    handleIsNotIntersecting,
    intersection,
    isEnabledImaManager,
    isImaInitialized,
    isManualPaused,
    isManualPlay,
    player,
    wrappedEnableAutoplay,
  ]);

  const handlePlayerReady = useCallback(
    async (event: { target: YouTubePlayer }) => {
      setPlayer(event.target);
      handleOnReady(event.target);

      await (isMuteVideo ? event.target.mute() : event.target.unMute());

      const playerMode = event.target.getPlayerMode?.();
      const shouldInitializeIma = isEnabledImaManager && playerMode?.pfp?.enableIma === true;

      if (shouldInitializeIma) {
        await initImaManager(event.target, uniqueVideoId, adElementId);
      } else {
        console.info("IMA: shouldn't be initialized", {
          isEnabledImaManager,
          pfp: playerMode?.pfp,
        });
        handleImaInitialized();
      }

      const iframe = await event.target.getIframe();
      setYoutubeIframe(iframe);
    },
    [
      adElementId,
      handleImaInitialized,
      handleOnReady,
      initImaManager,
      isEnabledImaManager,
      isMuteVideo,
      uniqueVideoId,
    ],
  );

  const handleStateChange = useCallback(
    (event: { data: number }) => {
      if (event.data === YouTube.PlayerState.PLAYING) {
        onPlaybackVisible?.();
      }
      handleOnYoutubePlayerStateChange(event);
      void handleOnStateChange(event);
    },
    [handleOnStateChange, handleOnYoutubePlayerStateChange, onPlaybackVisible],
  );

  return (
    <Container $orientation={orientation} className={className}>
      <VideoContainer
        onMouseLeave={handleOnYoutubeInteractEnd}
        onMouseOver={handleOnYoutubeInteractStart}
        onTouchEnd={handleOnYoutubeInteractEnd}
        onTouchStart={handleOnYoutubeInteractStart}
        ref={captureIntersectionElement}
      >
        <StyledYouTube
          id={uniqueVideoId}
          onEnd={handleEnded}
          onReady={handlePlayerReady}
          onStateChange={handleStateChange}
          opts={playerOptions}
          ref={playerRef}
          title={data?.content?.headline ?? ""}
          videoId={videoId}
        />
        <AdContainer id={adElementId} />
        {isAdStarted && (
          <AdControlButtonContainer>
            {isMute ? (
              <StyledUnmuteIcon onClick={() => handleChangeVolume(false)} />
            ) : (
              <StyledMuteIcon
                onClick={() => {
                  handleChangeVolume(true);
                  void handleMuteTracking();
                }}
              />
            )}
          </AdControlButtonContainer>
        )}
      </VideoContainer>
    </Container>
  );
};

SCMPYouTubeVideoPlayer.displayName = "SCMPYouTubeVideoPlayer";
